import { Float32BufferAttribute, Mesh, MeshBasicMaterial, } from "three";
import { RendererUtils } from "../RendererUtils";
import { WebMercatorProjection } from "../../math/projection";
/**
 * This IRenderPass implements the GPU side of the tile compositing pipeline.
 *
 * This pass works by functioning as an IRenderPass implementation as well as an interface for queueing compositing
 * work. To composite a tile you add a job to the queue, which will be resolved when IRenderPass.render is called.
 */
export class TileCompositorPass {
    constructor() {
        this.jobs = [];
        this.material = new MeshBasicMaterial({
            map: null,
            transparent: true,
        });
    }
    /**
     * Adds the given jobs to the job queue.
     * @param jobs
     */
    pushJobs(...jobs) {
        this.jobs.push(...jobs);
    }
    render(threeRenderer, webGLRenderer, camera, scene) {
        for (const job of this.jobs) {
            // Apply the source texture to the material
            this.material.map = job.src;
            // Perform reprojection from Web Mercator to Geograhpic if necessary
            let mesh;
            if (job.reprojectWebMercator) {
                const northMercatorY = WebMercatorProjection.geodeticLatitudeToMercatorAngle(job.rectangle.north);
                const southMercatorY = WebMercatorProjection.geodeticLatitudeToMercatorAngle(job.rectangle.south);
                const invMercatorHeight = 1.0 / (northMercatorY - southMercatorY);
                const uv = new Float32Array(2 * RendererUtils.GEOGRAHPIC_QUAD_Y_SAMPLES * 2);
                let u_index = 0;
                for (let i = 0; i < RendererUtils.GEOGRAHPIC_QUAD_Y_SAMPLES; ++i) {
                    const frac = i / (RendererUtils.GEOGRAHPIC_QUAD_Y_SAMPLES - 1);
                    const latitude = (1.0 - frac) * job.rectangle.south + frac * job.rectangle.north;
                    const mercatorY = WebMercatorProjection.geodeticLatitudeToMercatorAngle(latitude);
                    const mercatorFrac = (mercatorY - southMercatorY) * invMercatorHeight;
                    uv[u_index++] = 0.0;
                    uv[u_index++] = mercatorFrac;
                    uv[u_index++] = 1.0;
                    uv[u_index++] = mercatorFrac;
                }
                RendererUtils.GEOGRAPHIC_QUAD.setAttribute("uv", new Float32BufferAttribute(uv, 2, false));
                mesh = new Mesh(RendererUtils.GEOGRAPHIC_QUAD, this.material);
            }
            else {
                mesh = new Mesh(RendererUtils.FULLSCREEN_QUAD, this.material);
            }
            // Apply the job's requested tile transform
            mesh.position.set(job.offset.x, job.offset.y, 0);
            mesh.scale.set(job.scale.x, job.scale.y, 1);
            mesh.updateMatrixWorld(true);
            // Set the target texture and perform the composite
            webGLRenderer.setRenderTarget(job.dst);
            webGLRenderer.render(mesh, RendererUtils.UNIT_ORTHOGRAPHIC);
            if (job.disposeSrc) {
                job.src.dispose();
            }
        }
        this.jobs = [];
    }
}
